import { createContext, useContext, ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';

import { ConfirmDeleteModal } from '@labradorsports/components';
import { DocumentTypes } from '@labradorsports/constants';

import { Selectors, useDeleteTeamDocumentMutation } from '../store/index.js';

import { UploadDocumentModal, ViewDocumentModal } from './index.js';

export interface DocumentsContextValue {
    editDocument: (document: any) => void;
    viewDocument: (document: any) => void;
    deleteDocument: (document: any) => void;
    uploadDocument: () => void;
}

export const DocumentsContext = createContext<DocumentsContextValue>(null);

export const useDocuments = (): DocumentsContextValue => {
    return useContext(DocumentsContext);
};

interface Props {
    children: ReactNode;
}

export const DocumentsProvider: FC<Props> = ({ children }) => {
    const [deleteDocument] = useDeleteTeamDocumentMutation();
    const activeTeam = useSelector(Selectors.activeTeam);
    const [showingUploadDocument, setShowingUploadDocument] = useState(false);
    const [viewingDocument, setViewingDocument] = useState(null);
    const [editingDocument, setEditingDocument] = useState(null);
    const [deletingDocument, setDeletingDocument] = useState(null);

    const uploadDocument = () => setShowingUploadDocument(true);
    const cancelUploadDocument = () => {
        setShowingUploadDocument(false);
        setEditingDocument(null);
    };

    const cancelViewDocument = () => setViewingDocument(null);
    const cancelDeleteDocument = () => setDeletingDocument(null);

    const confirmDeleteDocument = async () => {
        await deleteDocument({ document: deletingDocument, activeTeam });

        cancelDeleteDocument();
    };

    const viewDocument = (document: any) => {
        if (document.type === DocumentTypes.LINK) {
            window.open(document.url, '_blank');
        } else {
            setViewingDocument(document);
        }
    };

    const value: DocumentsContextValue = {
        editDocument: setEditingDocument,
        deleteDocument: setDeletingDocument,
        viewDocument,
        uploadDocument,
    };

    return (
        <DocumentsContext.Provider value={value}>
            <UploadDocumentModal
                open={showingUploadDocument || Boolean(editingDocument)}
                onClose={cancelUploadDocument}
                editing={editingDocument}
            />
            <ViewDocumentModal
                open={Boolean(viewingDocument)}
                onClose={cancelViewDocument}
                document={viewingDocument}
            />
            <ConfirmDeleteModal
                open={Boolean(deletingDocument)}
                text="Are you sure you want to delete this document? Your team will no longer be able to access it. This cannot be undone."
                cancel={cancelDeleteDocument}
                confirm={confirmDeleteDocument}
            />
            {children}
        </DocumentsContext.Provider>
    );
};
